import { useMutation, useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { CircleDollarSign, Download, Info, Percent, Receipt } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { fileteDasAbas } from '@/componentes/filete';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { Seletor } from '@/componentes/Seletor';
import { PaginaSemPermissao } from '@/componentes/SemPermissao';
import { RodapeDeContagem, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarCompetencia, formatarInteiro, formatarReais } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { useSessao } from '@/modulos/sessao/consultas';

import { apiDoFinanceiro, type ChaveDoRecorte, type Dre, type GrupoDaDre, type LinhaDaDre, type MesDaDre, type Recorte } from './api';
import {
  competenciaDe,
  competenciasRecentes,
  csvDosLancamentos,
  fraseDaConferencia,
  janelaDaDre,
  margemEscrita,
  NOME_DO_RECORTE,
  PERIODOS_DA_DRE,
  periodoEscrito,
  RECORTES,
  reaisComSinal,
  validarBuscaDoFinanceiro,
  variacaoDaLinha,
  type AbaDoFinanceiro,
  type PeriodoDaDre,
} from './financeiro';
import { PlanoDeContas } from './PlanoDeContas';
import { TitulosDaOperacao } from './TitulosDaOperacao';

const rota = getRouteApi('/financeiro');

const ABAS: readonly (readonly [AbaDoFinanceiro, string])[] = [
  ['dre', 'DRE gerencial'],
  ['plano', 'Plano de contas'],
  ['titulos', 'Títulos'],
];

/**
 * MÓDULO 15 — FINANCEIRO GERENCIAL E DRE (0102).
 *
 * Três abas, uma ferramenta do v6 em cada, e a quarta (integração e
 * validação contábil) espalhada onde ela age: a conta contábil de cada linha,
 * a validação da contabilidade no plano e o arquivo dos lançamentos.
 *
 * Nenhum número nasce aqui. A DRE vem do servidor com o período anterior ao
 * lado, as premissas, a frase fiscal (o que a DRE gerencial não é) e a
 * conferência contra as telas de origem — e a tela imprime tudo como chegou.
 *
 * Sem desenho no manual (o mapa dos módulos, p.50 a p.60, não tem o 15): segue
 * o hub com abas (p.40), a faixa de indicadores (p.26) e a tabela com painel
 * de detalhe (p.38). Dúvida de design registrada no relatório.
 */
export function PaginaFinanceiro() {
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const pode = permissoes.includes('financeiro.ler');
  const gere = permissoes.includes('plano_de_contas.gerir');
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const aba: AbaDoFinanceiro = busca.aba ?? 'dre';
  const [criandoConta, setCriandoConta] = useState(false);
  /* A LINHA ABERTA (a chave do recorte ou a conta): o painel de detalhe mora AO
     LADO da lista (p.29), fora do contêiner que rola, e por isso o estado sobe
     para a página — como no piloto. Trocar de período, recorte ou aba fecha. */
  const [aberta, setAberta] = useState<string | null>(null);
  const [linhaAberta, setLinhaAberta] = useState<string | null>(null);
  const { recorte, janela } = useBuscaDaDre();
  const dre = useQuery({
    queryKey: ['financeiro', 'dre', janela.de, janela.ate, recorte],
    queryFn: () => apiDoFinanceiro.dre({ de: janela.de, ate: janela.ate, recorte }),
    enabled: aba === 'dre' && (aberta !== null || linhaAberta !== null),
  });
  const plano = useQuery({ queryKey: ['financeiro', 'plano'], queryFn: apiDoFinanceiro.plano, staleTime: 60_000, enabled: aba === 'dre' && linhaAberta !== null });
  const fecharPaineis = () => {
    setAberta(null);
    setLinhaAberta(null);
  };

  if (sessao.data && !pode) {
    return <PaginaSemPermissao sobretitulo="Financeiro gerencial" titulo="DRE e plano de contas" modulo="o financeiro gerencial e a DRE" licenca="financeiro.ler" />;
  }

  const d = dre.data;
  return (
    <>
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Financeiro gerencial"
          titulo="DRE e plano de contas"
          subtitulo="O resultado gerencial por competência, de onde cada linha vem, e os títulos que a operação conhece"
          acoes={
            aba === 'plano' && gere ? (
              <Botao
                nivel="primario"
                onClick={() => {
                  setCriandoConta(true);
                }}
              >
                Criar conta
              </Botao>
            ) : null
          }
        />

        <div role="tablist" aria-label="Seções do financeiro" ref={fileteDasAbas} className="barra-de-abas filete-das-abas">
          {ABAS.map(([id, rotulo]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={aba === id}
              onClick={() => {
                fecharPaineis();
                void navegar({ search: validarBuscaDoFinanceiro({ ...busca, aba: id === 'dre' ? undefined : id }) });
              }}
              className="aba"
            >
              {rotulo}
            </button>
          ))}
        </div>

        {aba === 'dre' ? <AbaDaDre aberta={aberta} aoAbrir={setAberta} linhaAberta={linhaAberta} aoAbrirLinha={setLinhaAberta} aoMudarFiltro={fecharPaineis} /> : null}
        {aba === 'plano' ? (
          <PlanoDeContas
            gere={gere}
            criandoConta={criandoConta && gere}
            aoFecharCriacao={() => {
              setCriandoConta(false);
            }}
          />
        ) : null}
        {aba === 'titulos' ? <TitulosDaOperacao /> : null}
      </div>
    </div>
      {aba === 'dre' && recorte !== 'empresa' && aberta ? (
        <PainelDaChave
          key={aberta}
          chave={aberta}
          recorte={recorte}
          de={janela.de}
          ate={janela.ate}
          linha={d?.porChave.find((l) => l.chave === aberta) ?? null}
          aoFechar={() => {
            setAberta(null);
          }}
        />
      ) : null}
      {aba === 'dre' && recorte === 'empresa' && linhaAberta && d ? (
        <PainelDaLinha
          key={linhaAberta}
          dre={d}
          id={linhaAberta}
          origens={plano.data?.origens ?? []}
          aoFechar={() => {
            setLinhaAberta(null);
          }}
        />
      ) : null}
    </>
  );
}

/** O período e o recorte da DRE, do endereço — a aba e o painel leem o mesmo. */
function useBuscaDaDre() {
  const busca = rota.useSearch();
  const corrente = competenciaDe(new Date());
  const meses: PeriodoDaDre = busca.meses ?? 12;
  const ate = busca.ate ? `${busca.ate}-01` : corrente;
  const recorte: Recorte = busca.recorte ?? 'empresa';
  return { corrente, meses, ate, recorte, janela: janelaDaDre(meses, ate) };
}

/* ================================================================== a DRE */

type LinhaDoDemonstrativo =
  | { readonly tipo: 'grupo'; readonly id: string; readonly grupo: GrupoDaDre; readonly sinal: '' | '(−)' }
  | { readonly tipo: 'linha'; readonly id: string; readonly grupo: GrupoDaDre; readonly linha: LinhaDaDre }
  | { readonly tipo: 'subtotal'; readonly id: string; readonly rotulo: string; readonly valor: number; readonly anterior: number; readonly margem: number | null };

function AbaDaDre({
  aberta,
  aoAbrir: setAberta,
  linhaAberta,
  aoAbrirLinha: setLinhaAberta,
  aoMudarFiltro,
}: {
  readonly aberta: string | null;
  readonly aoAbrir: (chave: string) => void;
  readonly linhaAberta: string | null;
  readonly aoAbrirLinha: (id: string) => void;
  readonly aoMudarFiltro: () => void;
}) {
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const { corrente, meses, ate, recorte, janela } = useBuscaDaDre();

  const dre = useQuery({
    queryKey: ['financeiro', 'dre', janela.de, janela.ate, recorte],
    queryFn: () => apiDoFinanceiro.dre({ de: janela.de, ate: janela.ate, recorte }),
    meta: { oQueLia: 'a DRE gerencial' },
  });
  /* O ALCANCE (robôs, rodada 3): `GET /financeiro/lancamentos` é da empresa
     inteira e recusa quem tem só parte dela (400 e toast) — o botão não é
     oferecido; a DRE já diz, no corpo, o que entra no alcance da pessoa. */
  const alcanceParcial = useSessao().data?.alcanceParcial === true;
  /* Exportar os lançamentos da competência final: uma leitura, e não gravação. */
  const exportar = useMutation({
    mutationFn: () => apiDoFinanceiro.lancamentos(janela.ate),
    meta: { silencioso: true },
    onSuccess: (r) => {
      const url = URL.createObjectURL(new Blob([csvDosLancamentos(r.lancamentos, r.escrituracao)], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `lancamentos-gerenciais-${janela.ate.slice(0, 7)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });

  const ir = (mudanca: Record<string, unknown>) => {
    aoMudarFiltro();
    void navegar({ search: validarBuscaDoFinanceiro({ ...busca, ...mudanca }) });
  };

  const d = dre.data;
  const atual = d?.resultado.atual;
  const antes = d?.resultado.anterior;
  const custoTotal = (t: Dre['resultado']['atual'] | undefined) =>
    t?.custo_direto === undefined ? null : (t.custo_direto ?? 0) + (t.custo_indireto ?? 0) + (t.depreciacao ?? 0);
  const variacao = (grupo: 'receita' | 'custo_direto' | 'resultado', a: number | null | undefined, b: number | null | undefined) =>
    a === null || a === undefined || b === null || b === undefined ? null : variacaoDaLinha(grupo, a, b);
  const comVariacao = (v: ReturnType<typeof variacao>) => (v ? { variacao: v.variacao, sentido: v.sentido } : {});
  const contextoDoPeriodo = d ? `${periodoEscrito(meses, d.periodo.de, ate)} · vs. ${formatarCompetencia(d.anterior.de)} a ${formatarCompetencia(d.anterior.ate)}` : '';

  return (
    <>
      <div className="flex flex-wrap items-end gap-4">
        <Seletor<`${PeriodoDaDre}`>
          rotulo="Período"
          valor={`${meses}`}
          opcoes={PERIODOS_DA_DRE.map((m) => ({ valor: `${m}` as `${PeriodoDaDre}`, rotulo: m === 1 ? '1 mês' : `${String(m)} meses` }))}
          aoEscolher={(v) => {
            ir({ meses: Number(v) === 12 ? undefined : Number(v) });
          }}
        />
        <div className="w-48">
          <CampoSelecao
            rotulo="Até a competência"
            valor={ate.slice(0, 7)}
            aoMudar={(v) => {
              ir({ ate: `${v}-01` === corrente ? undefined : v });
            }}
            opcoes={competenciasRecentes(corrente).map((c) => ({ valor: c.slice(0, 7), rotulo: formatarCompetencia(c) }))}
          />
        </div>
        <div className="w-56">
          <CampoSelecao
            rotulo="Recorte"
            valor={recorte}
            aplicado={recorte !== 'empresa'}
            aoMudar={(v) => {
              ir({ recorte: v === 'empresa' ? undefined : v });
            }}
            opcoes={RECORTES.map((r) => ({ valor: r, rotulo: NOME_DO_RECORTE[r] }))}
          />
        </div>
        {d && !d.soReceita && !alcanceParcial ? (
          <Botao
            nivel="secundario"
            className="ml-auto"
            disabled={exportar.isPending}
            onClick={() => {
              exportar.mutate();
            }}
          >
            <Download aria-hidden="true" className="size-4" />
            {exportar.isPending ? 'Exportando…' : `Exportar lançamentos de ${formatarCompetencia(janela.ate)}`}
          </Botao>
        ) : null}
      </div>
      {exportar.isError ? (
        <p role="alert" className="txt-apoio text-parada-texto">
          {explicarFalha(exportar.error).comoResolver}
        </p>
      ) : null}

      {dre.isError && !d ? (
        <ErroDaLista oQue="A DRE gerencial" erro={dre.error} aoTentarDeNovo={() => void dre.refetch()} />
      ) : (
        <>
          <FaixaIndicadores>
            <CardIndicador
              rotulo="Receita"
              icone={Receipt}
              tom="marca"
              carregando={!d}
              valor={atual ? formatarReais(atual.receita) : ''}
              contexto={contextoDoPeriodo || 'Medições aprovadas no período'}
              {...comVariacao(variacao('receita', atual?.receita, antes?.receita))}
            />
            {d?.soReceita ? (
              <div className="cartao flex flex-col justify-center gap-1 p-4 sm:col-span-3">
                <span className="txt-corpo text-tinta">Sem custo e sem resultado para o seu alcance</span>
                <span className="txt-apoio text-pretty text-tinta-secundaria">{d.premissas.naoInclui[0]}</span>
              </div>
            ) : (
              <>
                <CardIndicador
                  rotulo="Custo total"
                  icone={CircleDollarSign}
                  tom="neutro"
                  carregando={!d}
                  valor={atual ? formatarReais(custoTotal(atual) ?? 0) : ''}
                  contexto="Direto, indireto rateado e depreciação"
                  {...comVariacao(variacao('custo_direto', custoTotal(atual), custoTotal(antes)))}
                />
                <CardIndicador
                  rotulo="Margem de contribuição"
                  icone={Percent}
                  tom="disponivel"
                  carregando={!d}
                  valor={atual?.margemDeContribuicao === undefined ? '' : reaisComSinal(atual.margemDeContribuicao)}
                  alerta={(atual?.margemDeContribuicao ?? 0) < 0}
                  contexto={`Receita menos custos diretos · ${margemEscrita(atual?.margemDeContribuicaoPercentual)}`}
                  {...comVariacao(variacao('resultado', atual?.margemDeContribuicao, antes?.margemDeContribuicao))}
                />
                <CardIndicador
                  rotulo={d?.premissas.completo ? 'Resultado gerencial' : 'Resultado gerencial parcial'}
                  icone={Percent}
                  tom="operando"
                  carregando={!d}
                  valor={atual?.resultado === undefined ? '' : reaisComSinal(atual.resultado)}
                  alerta={(atual?.resultado ?? 0) < 0}
                  contexto={`Margem ${margemEscrita(atual?.margem)} · depois de indiretos e depreciação`}
                  {...comVariacao(variacao('resultado', atual?.resultado, antes?.resultado))}
                />
              </>
            )}
          </FaixaIndicadores>

          {d ? (
            <p className={`txt-apoio text-pretty ${d.conferencia.fecha ? 'text-tinta-secundaria' : 'text-parada-texto'}`}>{fraseDaConferencia(d.conferencia)}</p>
          ) : (
            <Carregando oQue="Carregando a conferência da DRE">
              <Barra className="my-1 h-3 w-3/5" />
            </Carregando>
          )}

          {recorte === 'empresa' ? (
            <Demonstrativo
              dre={d}
              carregando={dre.isPending}
              aberta={linhaAberta}
              aoAbrir={setLinhaAberta}
              rotuloDoPeriodo={d ? periodoEscrito(meses, d.periodo.de, ate) : 'Período'}
            />
          ) : (
            <TabelaDoRecorte
              recorte={recorte}
              linhas={d?.porChave ?? []}
              carregando={dre.isPending}
              soReceita={d?.soReceita ?? false}
              aberta={aberta}
              aoAbrir={setAberta}
            />
          )}

          <h2 className="txt-secao text-tinta">Como o resultado andou mês a mês?</h2>
          <MesAMes mensal={d?.mensal ?? []} carregando={dre.isPending} soReceita={d?.soReceita ?? false} />

          <Premissas dre={d} />
        </>
      )}

    </>
  );
}

/** As linhas da DRE, na ordem em que ela se lê, com os dois subtotais. */
function linhasDoDemonstrativo(d: Dre): LinhaDoDemonstrativo[] {
  const grupo = (g: string) => d.demonstrativo.find((x) => x.grupo === g);
  const linhas: LinhaDoDemonstrativo[] = [];
  const empurrar = (g: GrupoDaDre | undefined, sinal: '' | '(−)') => {
    if (!g) return;
    linhas.push({ tipo: 'grupo', id: `g-${g.grupo}`, grupo: g, sinal });
    for (const l of g.linhas) linhas.push({ tipo: 'linha', id: l.contaId ?? `sem-${l.origens.join('-')}`, grupo: g, linha: l });
  };
  empurrar(grupo('receita'), '');
  if (d.soReceita) return linhas;
  empurrar(grupo('custo_direto'), '(−)');
  const r = d.resultado;
  linhas.push({
    tipo: 'subtotal', id: 'margem-de-contribuicao', rotulo: '= Margem de contribuição',
    valor: r.atual.margemDeContribuicao ?? 0, anterior: r.anterior.margemDeContribuicao ?? 0, margem: r.atual.margemDeContribuicaoPercentual ?? null,
  });
  empurrar(grupo('custo_indireto'), '(−)');
  empurrar(grupo('depreciacao'), '(−)');
  linhas.push({
    tipo: 'subtotal', id: 'resultado', rotulo: d.premissas.completo ? '= Resultado gerencial' : '= Resultado gerencial parcial',
    valor: r.atual.resultado ?? 0, anterior: r.anterior.resultado ?? 0, margem: r.atual.margem ?? null,
  });
  return linhas;
}

function Demonstrativo({
  dre,
  carregando,
  aberta,
  aoAbrir,
  rotuloDoPeriodo,
}: {
  readonly dre: Dre | undefined;
  readonly carregando: boolean;
  readonly aberta: string | null;
  readonly aoAbrir: (id: string) => void;
  readonly rotuloDoPeriodo: string;
}) {
  const linhas = dre ? linhasDoDemonstrativo(dre) : [];
  const valorDe = (l: LinhaDoDemonstrativo, qual: 'valor' | 'anterior') =>
    l.tipo === 'grupo' ? l.grupo[qual] : l.tipo === 'linha' ? l.linha[qual] : l[qual];
  const grupoDe = (l: LinhaDoDemonstrativo) => (l.tipo === 'subtotal' ? 'resultado' : l.grupo.grupo);
  const forte = (l: LinhaDoDemonstrativo) => l.tipo !== 'linha';
  const colunas: readonly Coluna<LinhaDoDemonstrativo>[] = [
    {
      chave: 'conta',
      rotulo: 'Conta',
      prioridade: 'chave',
      celula: (l) =>
        l.tipo === 'linha' ? (
          <span className="flex min-w-0 items-baseline gap-2 pl-4">
            <span className="codigo text-tinta-secundaria">{l.linha.codigo ?? '—'}</span>
            <span className="flex min-w-0 flex-col">
              <span className="txt-corpo text-tinta">{l.linha.nome}</span>
              {l.linha.contaContabil ? <TextoDaCelula texto={`Conta contábil ${l.linha.contaContabil}`} classe="txt-apoio text-tinta-apagada" linhas={1} /> : null}
            </span>
          </span>
        ) : l.tipo === 'grupo' ? (
          <span className="txt-corpo font-semibold text-tinta">{`${l.sinal ? `${l.sinal} ` : ''}${l.grupo.rotulo}`}</span>
        ) : (
          <span className="txt-corpo font-semibold text-tinta">{l.rotulo}</span>
        ),
    },
    {
      chave: 'atual',
      rotulo: rotuloDoPeriodo,
      alinhamento: 'numero',
      celula: (l) => {
        const v = valorDe(l, 'valor');
        return (
          <span className="flex flex-col items-end">
            <span className={`numero whitespace-nowrap ${forte(l) ? 'font-semibold' : ''} ${l.tipo === 'subtotal' && v < 0 ? 'text-parada-texto' : 'text-tinta'}`}>{reaisComSinal(v)}</span>
            {l.tipo === 'subtotal' ? <span className="txt-apoio text-tinta-apagada">{`margem ${margemEscrita(l.margem)}`}</span> : null}
          </span>
        );
      },
    },
    {
      chave: 'anterior',
      rotulo: 'Período anterior',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (l) => <span className={`numero whitespace-nowrap text-tinta-secundaria ${forte(l) ? 'font-semibold' : ''}`}>{reaisComSinal(valorDe(l, 'anterior'))}</span>,
    },
    {
      chave: 'variacao',
      rotulo: 'Variação',
      alinhamento: 'numero',
      celula: (l) => {
        const v = variacaoDaLinha(grupoDe(l), valorDe(l, 'valor'), valorDe(l, 'anterior'));
        return v ? (
          <span className={`numero whitespace-nowrap ${v.sentido === 'melhorou' ? 'text-operando-texto' : 'text-parada-texto'}`}>{v.variacao.replace(' vs. período anterior', '')}</span>
        ) : (
          <span className="txt-apoio text-tinta-apagada">sem base</span>
        );
      },
    },
  ];
  return (
    <Tabela
      rotulo="Demonstrativo do resultado gerencial"
      colunas={colunas}
      linhas={linhas}
      chaveDaLinha={(l) => l.id}
      carregando={carregando}
      selecionada={aberta}
      aoSelecionar={(l) => {
        if (l.tipo === 'linha') aoAbrir(l.id);
      }}
      vazio={
        <EstadoVazio
          titulo="Nenhum valor no período"
          descricao="Sem medição aprovada e sem custo apurado, a DRE fica em branco. Escolha outro período, ou lance o custo e aprove a medição do mês."
        />
      }
      rodape={<RodapeDeContagem total={linhas.filter((l) => l.tipo === 'linha').length} />}
    />
  );
}

/** A tabela do recorte: uma linha por contrato, cliente, obra, centro ou máquina, e as do que não teve a quem pertencer. */
function TabelaDoRecorte({
  recorte,
  linhas,
  carregando,
  soReceita,
  aberta,
  aoAbrir,
}: {
  readonly recorte: Recorte;
  readonly linhas: readonly ChaveDoRecorte[];
  readonly carregando: boolean;
  readonly soReceita: boolean;
  readonly aberta: string | null;
  readonly aoAbrir: (chave: string) => void;
}) {
  const [pagina, setPagina] = useState(1);
  const porPagina = 20;
  const paginas = Math.max(1, Math.ceil(linhas.length / porPagina));
  const atual = Math.min(pagina, paginas);
  const visiveis = linhas.slice((atual - 1) * porPagina, atual * porPagina);
  const reais = (v: number | undefined, alerta = false) =>
    v === undefined ? <span className="text-tinta-secundaria">—</span> : <span className={`numero whitespace-nowrap ${alerta && v < 0 ? 'text-parada-texto' : 'text-tinta'}`}>{reaisComSinal(v)}</span>;
  const colunas: readonly Coluna<ChaveDoRecorte>[] = [
    {
      chave: 'nome',
      rotulo: NOME_DO_RECORTE[recorte],
      prioridade: 'chave',
      celula: (l) =>
        l.balde ? (
          <span className="txt-corpo text-tinta-secundaria">{l.nome}</span>
        ) : (
          <span className="flex min-w-0 flex-col">
            <span className="codigo text-tinta">{l.codigo ?? ''}</span>
            <TextoDaCelula texto={l.nome} classe="txt-apoio text-tinta-apagada" linhas={1} />
          </span>
        ),
    },
    { chave: 'receita', rotulo: 'Receita', alinhamento: 'numero', celula: (l) => reais(l.receita) },
    ...(soReceita
      ? []
      : ([
        { chave: 'diretos', rotulo: 'Custos diretos', alinhamento: 'numero', celula: (l) => reais(l.custo_direto) },
        { chave: 'contribuicao', rotulo: 'Margem de contribuição', alinhamento: 'numero', prioridade: 'secundaria', celula: (l) => reais(l.margemDeContribuicao, true) },
        {
          chave: 'indiretos',
          rotulo: 'Indiretos e depreciação',
          alinhamento: 'numero',
          prioridade: 'secundaria',
          celula: (l) => reais(l.custo_indireto === undefined ? undefined : (l.custo_indireto ?? 0) + (l.depreciacao ?? 0)),
        },
        { chave: 'resultado', rotulo: 'Resultado', alinhamento: 'numero', celula: (l) => reais(l.resultado, true) },
        {
          chave: 'margem',
          rotulo: 'Margem',
          alinhamento: 'numero',
          celula: (l) => <span className={`numero whitespace-nowrap ${(l.margem ?? 0) < 0 ? 'text-parada-texto' : 'text-tinta'}`}>{margemEscrita(l.margem)}</span>,
        },
      ] satisfies Coluna<ChaveDoRecorte>[])),
  ];
  return (
    <Tabela
      rotulo={`Resultado por ${NOME_DO_RECORTE[recorte].toLocaleLowerCase('pt-BR')}`}
      colunas={colunas}
      linhas={visiveis}
      chaveDaLinha={(l) => l.chave ?? `balde-${l.balde ?? ''}`}
      carregando={carregando}
      alturaDupla
      selecionada={aberta}
      aoSelecionar={(l) => {
        if (l.chave) aoAbrir(l.chave);
      }}
      vazio={
        <EstadoVazio
          titulo={`Nenhum ${NOME_DO_RECORTE[recorte].toLocaleLowerCase('pt-BR')} com valor no período`}
          descricao="Sem custo e sem receita no período, não há o que atribuir. Escolha outro período ou outro recorte."
        />
      }
      rodape={
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <span className="txt-apoio text-tinta-secundaria">{`Mostrando ${formatarInteiro(linhas.length === 0 ? 0 : (atual - 1) * porPagina + 1)} a ${formatarInteiro(Math.min(atual * porPagina, linhas.length))} de ${formatarInteiro(linhas.length)}`}</span>
          {paginas > 1 ? (
            <span className="flex gap-2">
              <Botao nivel="fantasma" tamanho="p" disabled={atual <= 1} onClick={() => { setPagina(atual - 1); }}>Página anterior</Botao>
              <Botao nivel="fantasma" tamanho="p" disabled={atual >= paginas} onClick={() => { setPagina(atual + 1); }}>Próxima página</Botao>
            </span>
          ) : null}
        </div>
      }
    />
  );
}

/** O demonstrativo de uma chave do recorte, no painel ao lado da lista. */
function PainelDaChave({
  chave,
  recorte,
  de,
  ate,
  linha,
  aoFechar,
}: {
  readonly chave: string;
  readonly recorte: Recorte;
  readonly de: string;
  readonly ate: string;
  readonly linha: ChaveDoRecorte | null;
  readonly aoFechar: () => void;
}) {
  const consulta = useQuery({
    queryKey: ['financeiro', 'dre', de, ate, recorte, chave],
    queryFn: () => apiDoFinanceiro.dre({ de, ate, recorte, chave }),
    meta: { oQueLia: 'a DRE do item escolhido' },
  });
  const d = consulta.data;
  const conteudo: ReactNode = consulta.isError ? (
    <ErroDaLista oQue="A DRE do item" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
  ) : !d ? (
    <Carregando oQue="Carregando a DRE do item" className="flex flex-col gap-2">
      <Barra className="h-4 w-full" />
      <Barra className="h-4 w-4/5" />
      <Barra className="h-4 w-3/5" />
    </Carregando>
  ) : (
    <ul className="flex flex-col divide-y divide-borda-sutil" aria-label="Linhas da DRE do item">
      {linhasDoDemonstrativo(d).map((l) => (
        <li key={l.id} className="flex items-baseline justify-between gap-3 py-2">
          <span className={`min-w-0 ${l.tipo === 'linha' ? 'txt-apoio pl-3 text-tinta-secundaria' : 'txt-corpo font-semibold text-tinta'}`}>
            {l.tipo === 'linha' ? `${l.linha.codigo ?? ''} ${l.linha.nome}` : l.tipo === 'grupo' ? `${l.sinal ? `${l.sinal} ` : ''}${l.grupo.rotulo}` : l.rotulo}
          </span>
          <span className={`numero shrink-0 whitespace-nowrap ${l.tipo === 'linha' ? 'text-tinta-secundaria' : 'font-semibold text-tinta'}`}>
            {reaisComSinal(l.tipo === 'grupo' ? l.grupo.valor : l.tipo === 'linha' ? l.linha.valor : l.valor)}
          </span>
        </li>
      ))}
    </ul>
  );
  return (
    <PainelDetalhe
      {...(linha?.codigo ? { codigo: linha.codigo } : {})}
      titulo={linha?.nome ?? d?.chave?.nome ?? NOME_DO_RECORTE[recorte]}
      subtitulo={`${NOME_DO_RECORTE[recorte]} · ${formatarCompetencia(de)} a ${formatarCompetencia(ate)}`}
      dadosChave={
        <>
          <DadoChave rotulo="Receita">{linha ? formatarReais(linha.receita) : '—'}</DadoChave>
          <DadoChave rotulo="Resultado">{linha?.resultado === undefined ? '—' : reaisComSinal(linha.resultado)}</DadoChave>
          <DadoChave rotulo="Margem">{margemEscrita(linha?.margem)}</DadoChave>
          <DadoChave rotulo="Período anterior">{d?.resultado.anterior.resultado === undefined ? '—' : reaisComSinal(d.resultado.anterior.resultado)}</DadoChave>
        </>
      }
      abas={[{ id: 'dre', rotulo: 'DRE', conteudo }]}
      aoFechar={aoFechar}
    />
  );
}

/** De onde vem a linha: as origens classificadas nela, cada uma com o módulo e a regra. */
function PainelDaLinha({
  dre,
  id,
  origens,
  aoFechar,
}: {
  readonly dre: Dre;
  readonly id: string;
  readonly origens: readonly { readonly origem: string; readonly rotulo: string; readonly modulo: string; readonly deOndeVem: string }[];
  readonly aoFechar: () => void;
}) {
  const linha = dre.demonstrativo.flatMap((g) => g.linhas.map((l) => ({ ...l, rotuloDoGrupo: g.rotulo }))).find((l) => (l.contaId ?? `sem-${l.origens.join('-')}`) === id);
  if (!linha) return null;
  const v = variacaoDaLinha(dre.demonstrativo.find((g) => g.linhas.some((l) => l.contaId === linha.contaId))?.grupo ?? 'custo_direto', linha.valor, linha.anterior);
  return (
    <PainelDetalhe
      {...(linha.codigo ? { codigo: linha.codigo } : {})}
      titulo={linha.nome}
      subtitulo={linha.rotuloDoGrupo}
      dadosChave={
        <>
          <DadoChave rotulo="No período">{reaisComSinal(linha.valor)}</DadoChave>
          <DadoChave rotulo="Período anterior">{reaisComSinal(linha.anterior)}</DadoChave>
          <DadoChave rotulo="Variação">{v ? v.variacao.replace(' vs. período anterior', '') : 'sem base'}</DadoChave>
          <DadoChave rotulo="Conta contábil">{linha.contaContabil ?? 'sem de-para com o ERP'}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'origem',
          rotulo: 'De onde vem',
          conteudo: (
            <ul className="flex flex-col gap-3" aria-label="Origens da linha">
              {linha.origens.length === 0 ? (
                <li className="txt-apoio text-tinta-secundaria">Nenhuma origem classificada nesta conta.</li>
              ) : (
                linha.origens.map((o) => {
                  const desc = origens.find((x) => x.origem === o);
                  return (
                    <li key={o} className="flex flex-col gap-1">
                      <span className="txt-corpo font-semibold text-tinta">{desc?.rotulo ?? o}</span>
                      <span className="txt-apoio text-tinta-secundaria">{desc ? `Módulo ${desc.modulo}` : ''}</span>
                      <span className="txt-apoio text-pretty text-tinta">{desc?.deOndeVem ?? ''}</span>
                    </li>
                  );
                })
              )}
            </ul>
          ),
        },
      ]}
      aoFechar={aoFechar}
    />
  );
}

/** O mês a mês do que está na tela. */
function MesAMes({ mensal, carregando, soReceita }: { readonly mensal: readonly MesDaDre[]; readonly carregando: boolean; readonly soReceita: boolean }) {
  const linhas = [...mensal].reverse();
  const reais = (v: number | undefined, alerta = false) =>
    v === undefined ? <span className="text-tinta-secundaria">—</span> : <span className={`numero whitespace-nowrap ${alerta && v < 0 ? 'text-parada-texto' : 'text-tinta'}`}>{reaisComSinal(v)}</span>;
  const colunas: readonly Coluna<MesDaDre>[] = [
    { chave: 'competencia', rotulo: 'Competência', celula: (m) => <span className="codigo text-tinta">{formatarCompetencia(m.competencia)}</span> },
    { chave: 'receita', rotulo: 'Receita', alinhamento: 'numero', celula: (m) => reais(m.receita) },
    ...(soReceita
      ? []
      : ([
        {
          chave: 'custo',
          rotulo: 'Custo total',
          alinhamento: 'numero',
          celula: (m) => reais(m.custo_direto === undefined ? undefined : (m.custo_direto ?? 0) + (m.custo_indireto ?? 0) + (m.depreciacao ?? 0)),
        },
        { chave: 'resultado', rotulo: 'Resultado', alinhamento: 'numero', celula: (m) => reais(m.resultado, true) },
        { chave: 'margem', rotulo: 'Margem', alinhamento: 'numero', celula: (m) => <span className="numero text-tinta">{margemEscrita(m.margem)}</span> },
      ] satisfies Coluna<MesDaDre>[])),
  ];
  return (
    <Tabela
      rotulo="Como o resultado andou mês a mês?"
      colunas={colunas}
      linhas={linhas}
      chaveDaLinha={(m) => m.competencia}
      carregando={carregando}
      vazio={<EstadoVazio titulo="Nenhum mês no período" descricao="Escolha o período e a competência final para ver o mês a mês." />}
      rodape={<RodapeDeContagem total={linhas.length} />}
    />
  );
}

/** O que entrou e o que ficou fora, e o que a DRE não é. */
function Premissas({ dre }: { readonly dre: Dre | undefined }) {
  if (!dre) {
    return (
      <Carregando oQue="Carregando as premissas da DRE" className="flex flex-col gap-2 py-1">
        <Barra className="h-3 w-full" />
        <Barra className="h-3 w-2/5" />
      </Carregando>
    );
  }
  return (
    <section className="cartao flex flex-col gap-4 p-6" aria-label="Premissas da DRE">
      <div className="flex items-start gap-3">
        <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-disponivel" />
        <p className="txt-corpo text-pretty text-tinta">{dre.escrituracao}</p>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="flex flex-col gap-2">
          <h2 className="txt-secao text-tinta">{dre.premissas.completo ? 'O que entrou' : 'O que ficou fora'}</h2>
          <ul className="txt-apoio flex list-disc flex-col gap-1 pl-5 text-pretty text-tinta-secundaria">
            {(dre.premissas.completo ? dre.premissas.inclui : dre.premissas.naoInclui).map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </div>
        {dre.premissas.completo ? null : (
          <div className="flex flex-col gap-2">
            <h2 className="txt-secao text-tinta">O que entrou</h2>
            <ul className="txt-apoio flex list-disc flex-col gap-1 pl-5 text-pretty text-tinta-secundaria">
              {dre.premissas.inclui.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
